import { CollectionEditor } from './CollectionEditor'
import { NavigationLayers } from './HomeNavigationEditor'
import { NativeAssetControl } from './NativeAssetControl'
import { NativeEventSearch } from './NativeEventSearch'
import { PlayerPropColumnsControl, PlayerPropScopeControl } from './PlayerPropBoardEditor'
import { ParlayBuilderLeaguesControl } from './ParlayBuilderEditor'
import { useEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { Activity, ArrowDown, ArrowUp, ChevronLeft, CopyPlus, Eye, EyeOff, GripVertical, Layers, Link2, List, Lock, Megaphone, Plus, Search, ShieldCheck, Sparkles, Table2, Trash2, Trophy, Video, Zap } from 'lucide-react'
import { Button, Field, HelpTip, IconButton, Input, Select, Switch, Textarea } from '@/components/common'
import { useQuery } from '@tanstack/react-query'
import { surfacesApi } from '@/services/api'
import { HOME_CARD_DRAG_TYPE, HOME_COMPONENT_DRAG_TYPE, HOME_LIBRARY_DRAG_TYPE, crossedMidpoint } from './nativeDnd'
import { CATALOG_SCOPES, EVENT_ROUTE_PREFIX, ROUTE_OPTIONS, availableComponentDefinitions, cardKindDefinition, cardKinds, cardName, componentDefinition, componentInstanceName, componentName } from './nativeSportsbookManifest'
import { PREVIEW_CATALOG, americanOdds, americanToMillis, bettableMarkets, cardScheduleState, outcomeReference, resolveLiveGameEvent } from './nativePreviewModel'
import { cardDuplicationIssue, sectionDuplicationIssue } from './nativeDuplication'
import { collectionChoices } from './collectionModel'
import styles from './nativeSportsbook.module.css'

const COMPONENT_ICONS = {
  sports_search: Search,
  hero: Megaphone,
  event_list: List,
  live_game_momentum: Activity,
  featured_showcase: Layers,
  compliance_notice: ShieldCheck,
  betwire_surface: Zap,
  competition_markets: Trophy,
  player_prop_board: Table2,
  same_game_parlay_builder: Link2,
}

const CARD_ICONS = {
  promotion: Megaphone,
  event_spotlight: Sparkles,
  video: Video,
  odds_boost: Zap,
  parlay_suggestion: Layers,
}

function ComponentIcon({ type, size = 14, className }) {
  const Icon = COMPONENT_ICONS[type] || List
  return <Icon size={size} className={className} aria-hidden="true" />
}

function CardIcon({ kind, size = 13, className }) {
  const Icon = CARD_ICONS[kind] || Sparkles
  return <Icon size={size} className={className} aria-hidden="true" />
}

function LibraryChip({ definition, disabled, onAdd }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: HOME_LIBRARY_DRAG_TYPE,
    item: { type: definition.type },
    canDrag: !disabled,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [definition.type, disabled])
  return (
    <button ref={drag} type="button" className={styles.libraryChip} disabled={disabled} data-dragging={isDragging ? 'true' : 'false'} title={definition.description} onClick={() => onAdd(definition.type)}>
      <Plus size={13} aria-hidden="true" />
      <ComponentIcon type={definition.type} size={13} />
      {definition.display_name}
    </button>
  )
}

function CardRow({ componentId, componentIndex, card, index, count, selected, disabled, onSelect, onMoveTo, onRemove, onDuplicate, duplicationIssue }) {
  const rowRef = useRef(null)
  const name = cardName(card)
  const kind = cardKindDefinition(card.kind)
  const schedule = cardScheduleState(card)
  const [{ isDragging }, drag] = useDrag(() => ({
    type: HOME_CARD_DRAG_TYPE,
    item: { componentId, componentIndex, index },
    canDrag: !disabled,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [componentId, componentIndex, index, disabled])
  const [, drop] = useDrop(() => ({
    accept: HOME_CARD_DRAG_TYPE,
    hover: (item, monitor) => {
      if (disabled || item.componentId !== componentId || item.index === index) return
      if (!crossedMidpoint(rowRef.current, monitor, item.index, index)) return
      if (onMoveTo(componentIndex, item.index, index)) item.index = index
    },
  }), [disabled, componentId, componentIndex, index, onMoveTo])
  drag(drop(rowRef))
  return (
    <li ref={rowRef} className={`${styles.layer} ${styles.cardLayer}`} data-selected={selected ? 'true' : 'false'} data-dragging={isDragging ? 'true' : 'false'} data-schedule={schedule}>
      <span className={styles.layerHandle} aria-hidden="true"><GripVertical size={13} /></span>
      <button type="button" className={styles.layerSelect} aria-current={selected ? 'true' : undefined} onClick={() => onSelect(componentId, card.id)}>
        <CardIcon kind={card.kind} className={styles.layerIcon} />
        <span className={styles.layerText}>
          <strong>{name}</strong>
          <small>{kind?.display_name || card.kind}{schedule !== 'active' ? ` · ${schedule}` : ''}</small>
        </span>
      </button>
      <span className={styles.layerActions}>
        <IconButton variant="ghost" className={styles.rowIcon} label={`Duplicate card ${name}`} title={duplicationIssue || `Duplicate card ${name}`} aria-description={duplicationIssue || undefined} disabled={disabled || Boolean(duplicationIssue)} onClick={() => onDuplicate(componentIndex, index)}><CopyPlus size={13} /></IconButton>
        <IconButton variant="ghost" className={styles.rowIcon} label={`Remove card ${name}`} disabled={disabled || count <= 1} onClick={() => onRemove(componentIndex, index)}><Trash2 size={13} /></IconButton>
      </span>
    </li>
  )
}

function CardLayers({ component, componentIndex, expanded, selectedCardId, disabled, onSelectCard, onAddCard, onMoveCardTo, onRemoveCard, onDuplicateCard }) {
  const kinds = cardKinds(component.type)
  const definition = componentDefinition(component.type)
  const full = component.cards.length >= (definition?.editor?.cards?.max_cards ?? 0)
  return (
    <div className={styles.cardLayers}>
      <ol className={styles.layers} aria-label={`${componentInstanceName(component)} cards`}>
        {component.cards.map((card, index) => (
          <CardRow key={card.id} componentId={component.id} componentIndex={componentIndex} card={card} index={index} count={component.cards.length} selected={card.id === selectedCardId} disabled={disabled} onSelect={onSelectCard} onMoveTo={onMoveCardTo} onRemove={onRemoveCard} onDuplicate={onDuplicateCard} duplicationIssue={cardDuplicationIssue(component)} />
        ))}
      </ol>
      {expanded && !disabled ? (
        <div className={styles.cardLibrary} role="group" aria-label={`Add a card to ${componentInstanceName(component)}`}>
          {kinds.map((kind) => (
            <button key={kind.kind} type="button" className={styles.libraryChip} disabled={full} title={kind.description} onClick={() => onAddCard(componentIndex, kind.kind)}>
              <Plus size={12} aria-hidden="true" /><CardIcon kind={kind.kind} size={12} />{kind.display_name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function LayerRow({ component, index, selected, cardSelected, disabled, onSelect, onMoveTo, onToggleVisible, onRemove, onDuplicate, duplicationIssue, cardProps }) {
  const rowRef = useRef(null)
  const definition = componentDefinition(component.type)
  const locked = !definition?.policy?.movable
  const enabled = component.enabled !== false
  const name = componentInstanceName(component)
  const [{ isDragging }, drag] = useDrag(() => ({
    type: HOME_COMPONENT_DRAG_TYPE,
    item: { index, id: component.id },
    canDrag: !disabled && !locked,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [disabled, locked, index, component.id])
  const [, drop] = useDrop(() => ({
    accept: HOME_COMPONENT_DRAG_TYPE,
    hover: (item, monitor) => {
      if (disabled || locked || item.index === index) return
      if (!crossedMidpoint(rowRef.current, monitor, item.index, index)) return
      if (onMoveTo(item.index, index)) item.index = index
    },
  }), [disabled, locked, index, onMoveTo])
  drag(drop(rowRef))
  return (
    <li className={styles.layerGroup} data-dragging={isDragging ? 'true' : 'false'}>
      <div ref={rowRef} className={styles.layer} data-selected={selected && !cardSelected ? 'true' : 'false'} data-hidden={enabled ? 'false' : 'true'} data-locked={locked ? 'true' : 'false'}>
        <span className={styles.layerHandle} aria-hidden="true">{locked ? <Lock size={13} /> : <GripVertical size={14} />}</span>
        <button type="button" className={styles.layerSelect} aria-current={selected && !cardSelected ? 'true' : undefined} onClick={() => onSelect(component.id)}>
          <ComponentIcon type={component.type} className={styles.layerIcon} />
          <span className={styles.layerText}>
            <strong>{name}</strong>
            <small>{componentName(component)}</small>
          </span>
        </button>
        <span className={styles.layerActions}>
          {definition?.policy?.removable && definition.policy.max_instances > 1 ? <IconButton variant="ghost" className={styles.rowIcon} label={`Duplicate section ${name}`} title={duplicationIssue || `Duplicate section ${name}`} aria-description={duplicationIssue || undefined} disabled={disabled || Boolean(duplicationIssue)} onClick={() => onDuplicate(index)}><CopyPlus size={14} /></IconButton> : null}
          {definition?.policy?.required
            ? <span className={styles.layerRequired}>Required</span>
            : <IconButton variant="ghost" className={styles.rowIcon} label={enabled ? `Hide ${name}` : `Show ${name}`} disabled={disabled} onClick={() => onToggleVisible(index, !enabled)}>{enabled ? <Eye size={14} /> : <EyeOff size={14} />}</IconButton>}
          {definition?.policy?.removable ? <IconButton variant="ghost" className={styles.rowIcon} label={`Remove ${name}`} disabled={disabled} onClick={() => onRemove(index)}><Trash2 size={14} /></IconButton> : null}
        </span>
      </div>
      {Array.isArray(component.cards) ? <CardLayers component={component} componentIndex={index} expanded={selected} disabled={disabled} {...cardProps} /> : null}
    </li>
  )
}

export function LayersPanel({ screen = null, navigation, showNavigation = true, components, registry, selectedId, selectedCardId = null, disabled = false, onSelect, onMoveTo, onToggleVisible, onRemove, onAdd, onSelectCard, onAddCard, onMoveCardTo, onRemoveCard, onDuplicate, onDuplicateCard, tools = null }) {
  const available = availableComponentDefinitions({ screens: [{ ...(screen || {}), components }] }, registry)
  const visibleCount = components.filter((component) => component.enabled !== false).length
  const cardProps = { selectedCardId, onSelectCard, onAddCard, onMoveCardTo, onRemoveCard, onDuplicateCard }
  return (
    <section className={`${styles.panel} ${styles.layersArea}`} aria-label="Layers">
      <header className={styles.panelHeader}><h2>Layers</h2><span>{visibleCount} of {components.length} visible</span></header>
      {showNavigation ? <NavigationLayers navigation={navigation} selectedId={selectedId} onSelect={onSelect} /> : null}
      <div className={styles.library} role="group" aria-label="Add a section">
        {available.map((definition) => <LibraryChip key={definition.type} definition={definition} disabled={disabled} onAdd={onAdd} />)}
        {available.length === 0 ? <span className={styles.libraryEmpty}>Every section type is already placed.</span> : null}
      </div>
      <ol className={styles.layers}>
        {components.map((component, index) => (
          <LayerRow key={component.id} component={component} index={index} selected={component.id === selectedId} cardSelected={component.id === selectedId && Boolean(selectedCardId)} disabled={disabled} onSelect={onSelect} onMoveTo={onMoveTo} onToggleVisible={onToggleVisible} onRemove={onRemove} onDuplicate={onDuplicate} duplicationIssue={sectionDuplicationIssue({ screens: [{ ...(screen || {}), components }] }, component, registry)} cardProps={cardProps} />
        ))}
      </ol>
      {tools ? <footer className={styles.panelFooter}>{tools}</footer> : null}
    </section>
  )
}

function pathValue(target, path) {
  return path.split('.').reduce((value, segment) => value?.[segment], target)
}

function toLocalDateTimeInput(iso) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fieldLabel(field) {
  return field.help ? { label: field.label, labelExtra: <HelpTip text={field.help} label={`About ${field.label}`} /> } : { label: field.label }
}

function RouteControl({ field, value, disabled, catalog, slug, onChange }) {
  const isEvent = typeof value === 'string' && value.startsWith(EVENT_ROUTE_PREFIX)
  const routeOptions = [...ROUTE_OPTIONS, ...(catalog.hub_routes ?? [])]
  const known = routeOptions.some((option) => option.value === value)
  const selectValue = isEvent ? 'event' : known ? value : value ? 'custom' : ''
  const eventId = isEvent ? value.slice(EVENT_ROUTE_PREFIX.length) : ''
  return (
    <div className={styles.routeControl}>
      <Field {...fieldLabel(field)}>
        <Select size="compact" value={selectValue} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value === 'event' ? EVENT_ROUTE_PREFIX : event.target.value)}>
          <option value="">Choose a destination</option>
          {routeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          <option value="event">An event…</option>
          {selectValue === 'custom' ? <option value="custom">{value} (legacy)</option> : null}
        </Select>
      </Field>
      {isEvent ? <NativeEventSearch label="Destination event" value={eventId} event={catalog.events.find((event) => event.id === eventId) || null} disabled={disabled} catalog={catalog} slug={slug} onSelect={(event) => onChange(field.path, `${EVENT_ROUTE_PREFIX}${event.id}`)} onClear={() => onChange(field.path, EVENT_ROUTE_PREFIX)} /> : null}
    </div>
  )
}

// Event, then market, then selection. The game comes from a catalog search; its
// markets and prices come from the same catalog entry the preview renders. Only
// open, priced outcomes are offered; a saved pick that has since been suspended
// stays visible with a warning so the operator sees what customers will not.
function OutcomePicker({ label, labelExtra, value, disabled, catalog, slug, onChange }) {
  const event = catalog.events.find((candidate) => candidate.id === value.event_id) || null
  const open = bettableMarkets(event)
  const savedMarket = event?.markets.find((candidate) => candidate.id === value.market_id) || null
  const savedOpen = open.some((candidate) => candidate.id === value.market_id && candidate.outcomes.some((outcome) => outcome.id === value.outcome_id))
  // A saved-but-unavailable market or outcome stays listed so the control tells
  // the truth; its unavailable siblings do not become selectable.
  const markets = savedMarket && !open.some((candidate) => candidate.id === savedMarket.id) ? [...open, savedMarket] : open
  const market = markets.find((candidate) => candidate.id === value.market_id) || null
  const bettableOutcomes = open.find((candidate) => candidate.id === market?.id)?.outcomes || []
  const savedOutcome = savedMarket?.outcomes.find((candidate) => candidate.id === value.outcome_id)
  const outcomes = savedOutcome && !bettableOutcomes.some((candidate) => candidate.id === savedOutcome.id) ? [...bettableOutcomes, savedOutcome] : bettableOutcomes
  const selectEvent = (nextEvent) => {
    const nextMarket = bettableMarkets(nextEvent)[0]
    onChange({ event_id: nextEvent.id, market_id: nextMarket?.id || '', outcome_id: nextMarket?.outcomes[0]?.id || '' })
  }
  const selectMarket = (marketId) => {
    const nextMarket = open.find((candidate) => candidate.id === marketId)
    onChange({ event_id: value.event_id, market_id: marketId, outcome_id: nextMarket?.outcomes[0]?.id || '' })
  }
  const rehearsal = catalog.rehearsal === 'unavailable_markets'
  return (
    <div className={styles.outcomePicker}>
      <NativeEventSearch label={label} labelExtra={labelExtra} value={value.event_id || ''} event={event} disabled={disabled} catalog={catalog} slug={slug} onSelect={selectEvent} onClear={() => onChange({ event_id: '', market_id: '', outcome_id: '' })} />
      {event && markets.length ? <>
        <Select size="compact" aria-label={`${label} market`} value={value.market_id || ''} disabled={disabled} onChange={(changed) => selectMarket(changed.target.value)}>
          {markets.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}
        </Select>
        <Select size="compact" aria-label={`${label} selection`} value={value.outcome_id || ''} disabled={disabled || !market} onChange={(changed) => onChange({ ...value, outcome_id: changed.target.value })}>
          {outcomes.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label} ({americanOdds(candidate.odds)})</option>)}
        </Select>
      </> : null}
      {event && !open.length ? <p className={styles.compatNote} role="alert">{rehearsal ? 'Markets are unavailable in this rehearsal.' : 'No open markets are posted for this game yet, so there is nothing to select. Choose another game.'}</p> : null}
      {event && open.length && value.outcome_id && !savedOpen ? <p className={styles.compatNote} role="alert">This selection is suspended or unpriced right now. Customers see the card only while it is open.</p> : null}
    </div>
  )
}

function AmericanOddsControl({ field, value, current, disabled, onChange }) {
  const [text, setText] = useState(() => (value ? americanOdds(value) : ''))
  // The draft can change under the control (a loaded revision, a save response)
  // without remounting it, so typed text is replaced whenever the value moves.
  const [synced, setSynced] = useState(value)
  if (synced !== value) {
    setSynced(value)
    setText(value ? americanOdds(value) : '')
  }
  const commit = () => {
    const millis = americanToMillis(text)
    if (!millis) { setText(value ? americanOdds(value) : ''); return }
    if (millis !== value) onChange(field.path, millis)
    setText(americanOdds(millis))
  }
  return (
    <Field {...fieldLabel(field)} help={current ? `Market price now ${americanOdds(current)}.` : undefined}>
      <Input size="compact" className={styles.oddsInput} inputMode="text" value={text} disabled={disabled} placeholder="+150" onChange={(event) => setText(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commit() } }} />
    </Field>
  )
}

// An integer number of seconds, or off (stored as 0 so both native clients read
// it as "leave the customer in control").
function IntervalSecondsControl({ field, value, disabled, onChange }) {
  const min = field.min ?? 1
  const max = field.max ?? 60
  const clamp = (candidate) => Math.min(max, Math.max(min, candidate))
  const seconds = Number.isInteger(value) && value > 0 ? value : 0
  const [text, setText] = useState(seconds ? String(seconds) : '')
  // The draft can change under the control (a loaded revision, a save response)
  // without remounting it, so typed text is replaced whenever the value moves.
  const [synced, setSynced] = useState(seconds)
  if (synced !== seconds) {
    setSynced(seconds)
    setText(seconds ? String(seconds) : '')
  }
  const commit = () => {
    const parsed = Number.parseInt(text, 10)
    if (Number.isInteger(parsed) && parsed > 0) {
      const next = clamp(parsed)
      if (next !== seconds) onChange(field.path, next)
      setText(String(next))
    } else {
      setText(seconds ? String(seconds) : '')
    }
  }
  const toggle = (checked) => {
    const next = checked ? clamp(field.default ?? min) : 0
    onChange(field.path, next)
    setText(next ? String(next) : '')
  }
  return (
    <fieldset className={styles.intervalControl} disabled={disabled}>
      <legend>{field.label}{field.help ? <HelpTip text={field.help} label={`About ${field.label}`} /> : null}</legend>
      <div className={styles.intervalRow}>
        <Switch label={seconds ? 'On' : 'Off'} aria-label={field.label} checked={seconds > 0} disabled={disabled} onChange={toggle} />
        {seconds > 0 ? (
          <div className={styles.intervalSeconds}>
            <span>every</span>
            <Input size="compact" type="number" inputMode="numeric" min={min} max={max} step={1} value={text} disabled={disabled} aria-label={`${field.label} seconds`} onChange={(event) => setText(event.target.value)} onBlur={commit} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commit() } }} />
            <span>seconds</span>
          </div>
        ) : null}
      </div>
    </fieldset>
  )
}

// A new leg is empty until the operator picks its game; defaulting to the first
// catalog game wrote a selection nobody chose into the ticket.
function ParlayLegsControl({ field, value, disabled, catalog, slug, onChange }) {
  const legs = Array.isArray(value) ? value : []
  const update = (index, leg) => onChange(field.path, legs.map((candidate, position) => (position === index ? leg : candidate)))
  const remove = (index) => onChange(field.path, legs.filter((_, position) => position !== index))
  const add = () => onChange(field.path, [...legs, { event_id: '', market_id: '', outcome_id: '' }])
  return (
    <fieldset className={styles.legsControl} disabled={disabled}>
      <legend>{field.label}{field.help ? <HelpTip text={field.help} label={`About ${field.label}`} /> : null}</legend>
      {legs.map((leg, index) => (
        <div key={index} className={styles.legRow}>
          <OutcomePicker label={`Leg ${index + 1}`} value={leg} disabled={disabled} catalog={catalog} slug={slug} onChange={(next) => update(index, next)} />
          <IconButton variant="ghost" className={styles.rowIcon} label={`Remove leg ${index + 1}`} disabled={disabled || legs.length <= 2} onClick={() => remove(index)}><Trash2 size={13} /></IconButton>
        </div>
      ))}
      <Button size="compact" variant="secondary" disabled={disabled || legs.length >= 6} onClick={add}><Plus size={13} aria-hidden="true" />Add leg</Button>
    </fieldset>
  )
}

function SearchQueriesControl({ field, value, disabled, onChange }) {
  const [text, setText] = useState((value || []).join('\n'))
  const external = JSON.stringify(value || [])
  const published = useRef(external)
  useEffect(() => {
    if (external !== published.current) {
      published.current = external
      setText(JSON.parse(external).join('\n'))
    }
  }, [external])
  const queries = text.split('\n').map((query) => query.trim()).filter(Boolean)
  const error = queries.length > 5 ? 'Use up to five starting queries.'
    : new Set(queries).size !== queries.length ? 'Each starting query must be unique.'
      : queries.some((query) => Array.from(query).length < 2 || Array.from(query).length > 60) ? 'Each starting query must contain 2–60 characters.' : undefined
  return <Field {...fieldLabel(field)} error={error}><Textarea size="compact" rows={5} value={text} maxLength={304} disabled={disabled} onChange={(event) => {
    setText(event.target.value)
    const queries = event.target.value.split('\n').map((query) => query.trim()).filter(Boolean)
    published.current = JSON.stringify(queries)
    onChange(field.path, queries)
  }} /></Field>
}
function FeedSurfaceControl({ field, value, disabled, slug, onChange }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['surfaces', slug],
    queryFn: () => surfacesApi.list(slug),
    enabled: Boolean(slug),
    staleTime: 30_000,
  })
  const surfaces = (data?.items || []).filter((item) => item.is_enabled)
  const unknownValue = value && !surfaces.some((item) => item.id === value)
  return (
    <Field {...fieldLabel(field)} error={error ? 'Failed to load feed surfaces' : undefined}>
      <Select
        size="compact"
        value={value || ''}
        disabled={disabled || isLoading}
        onChange={(event) => onChange(field.path, event.target.value)}
      >
        <option value="">Choose a feed surface…</option>
        {surfaces.map((surface) => (
          <option key={surface.id} value={surface.id}>
            {surface.name || surface.slug}
          </option>
        ))}
        {unknownValue ? <option value={value}>{value} (unavailable)</option> : null}
      </Select>
    </Field>
  )
}


function EditorControl({ target, field, screen, disabled, catalog, slug, onChange }) {
  let value = pathValue(target, field.path) ?? field.default
  if (target?.type === 'competition_markets') {
    if (field.path === 'competition.sport' && (value === undefined || value === '') && screen?.context?.sport) {
      value = screen.context.sport
    }
    if (field.path === 'competition.name' && (value === undefined || value === '') && screen?.context?.competition) {
      value = screen.context.competition
    }
  }
  const hasHubContext = Boolean(screen?.context?.kind === 'competition' || (screen?.context?.sport && screen?.context?.competition))
  const isReadOnly = target?.type === 'competition_markets' && hasHubContext && (field.path === 'competition.sport' || field.path === 'competition.name')
  const coerce = (raw) => {
    if (field.coerce === 'integer') return raw === '' ? '' : Number.parseInt(raw, 10)
    if (field.coerce === 'boolean') return raw === 'true'
    return raw
  }
  // Competition markets need the provider's exact sport family and competition
  // name; offer the ones the current inventory knows instead of a blind text box.
  if (target?.type === 'competition_markets' && !isReadOnly && (field.path === 'competition.sport' || field.path === 'competition.name')) {
    const known = collectionChoices(catalog.events)
    const source = catalog.choices || {}
    const suggestions = field.path === 'competition.sport'
      ? [...new Set([...known.sports, ...(source.sports || [])])].sort()
      : [...new Set([...known.competition_scopes, ...(source.competition_scopes || [])].filter((scope) => !target.competition?.sport || scope.sport === target.competition.sport).map((scope) => scope.name))].sort()
    const listId = `${target.id}-${field.path}-suggestions`
    return <>
      <Field {...fieldLabel(field)}><Input size="compact" list={listId} value={value || ''} maxLength={field.max ?? 80} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value)} /></Field>
      <datalist id={listId}>{suggestions.map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>
    </>
  }
  switch (field.control) {
    case 'player_prop_scope':
      return <PlayerPropScopeControl target={target} screen={screen} catalog={catalog} slug={slug} disabled={disabled} onChange={onChange} />
    case 'player_prop_columns':
      return <PlayerPropColumnsControl target={target} catalog={catalog} slug={slug} disabled={disabled} onChange={onChange} />
    case 'parlay_builder_leagues':
      return <ParlayBuilderLeaguesControl key={target.id} target={target} screen={screen} catalog={catalog} slug={slug} disabled={disabled} onChange={onChange} />
    case 'feed_surface':
      return <FeedSurfaceControl key={target.id} field={field} value={value} disabled={disabled} slug={slug} onChange={onChange} />
    case 'select':
      return (
        <Field {...fieldLabel(field)}>
          <Select
            size="compact"
            value={value === undefined || value === null ? '' : String(value)}
            disabled={disabled}
            onChange={(event) => {
              const coerced = coerce(event.target.value)
              onChange(field.path, coerced)
              if (target?.type === 'competition_markets' && field.path === 'kind') {
                if (coerced === 'futures' && (!target.title || target.title === 'Player props')) {
                  onChange('title', 'Futures')
                } else if (coerced === 'player_props' && (!target.title || target.title === 'Futures')) {
                  onChange('title', 'Player props')
                }
              }
            }}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
      )
    case 'event_picker': {
      const event = catalog.events.find((candidate) => candidate.id === value) || null
      const live = field.scope !== 'any'
      // The app falls back per unavailable_behavior; say so here instead of
      // letting the preview silently show a different game than the pick.
      let help, missingMessage
      if (live && value && (!event || !event.live)) {
        const shown = resolveLiveGameEvent(target, catalog)
        help = shown ? `Showing ${shown.name} instead until this game is live.` : 'Hidden until a matching game is live.'
        missingMessage = 'Not in the current games.'
      }
      return <NativeEventSearch {...fieldLabel(field)} help={help} missingMessage={missingMessage} value={value || ''} event={event} state={live ? 'live' : 'active'} disabled={disabled} catalog={catalog} slug={slug} onSelect={(next) => onChange(field.path, next.id)} onClear={() => onChange(field.path, '')} />
    }
    case 'sport_multiselect': {
      const selected = Array.isArray(value) ? value : []
      const choices = CATALOG_SCOPES.filter((scope) => !scope.value.includes(','))
      const toggle = (scope) => onChange(field.path, selected.includes(scope) ? selected.filter((candidate) => candidate !== scope) : [...selected, scope])
      return (
        <fieldset className={styles.scopeChips} disabled={disabled}>
          <legend>{field.label}</legend>
          <div>{choices.map((scope) => <button key={scope.value} type="button" aria-pressed={selected.includes(scope.value)} onClick={() => toggle(scope.value)}>{scope.label}</button>)}</div>
        </fieldset>
      )
    }
    case 'query_list':
      return <SearchQueriesControl key={target.id} field={field} value={value} disabled={disabled} onChange={onChange} />
    case 'textarea':
      return <Field {...fieldLabel(field)}><Textarea size="compact" className={styles.inspectorTextarea} value={value || ''} maxLength={180} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value)} /></Field>
    case 'route':
      return <RouteControl field={field} value={value} disabled={disabled} catalog={catalog} slug={slug} onChange={onChange} />
    case 'url':
      if (['image_url', 'video_url', 'poster_url'].includes(field.path)) return <NativeAssetControl slug={slug} field={field} value={value} disabled={disabled} onChange={onChange} />
      return <Field {...fieldLabel(field)}><Input size="compact" type="url" value={value || ''} maxLength={2000} disabled={disabled} placeholder="https://" onChange={(event) => onChange(field.path, event.target.value.trim())} /></Field>
    case 'datetime':
      return <Field {...fieldLabel(field)}><Input size="compact" type="datetime-local" value={toLocalDateTimeInput(value)} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value ? new Date(event.target.value).toISOString() : '')} /></Field>
    case 'hex_color':
      return (
        <div className={styles.colorControl}>
          <Field {...fieldLabel(field)}>
            <input type="color" value={value || '#A700C4'} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value.toUpperCase())} />
          </Field>
          <span>{value || 'Theme accent'}</span>
          {value ? <Button size="compact" variant="ghost" disabled={disabled} onClick={() => onChange(field.path, '')}>Use theme accent</Button> : null}
        </div>
      )
    case 'outcome_picker': {
      const { labelExtra } = fieldLabel(field)
      return <OutcomePicker label={field.label} labelExtra={labelExtra} value={{ event_id: target.event_id, market_id: target.market_id, outcome_id: target.outcome_id }} disabled={disabled} catalog={catalog} slug={slug} onChange={(next) => { onChange('event_id', next.event_id); onChange('market_id', next.market_id); onChange('outcome_id', next.outcome_id) }} />
    }
    case 'american_odds':
      return <AmericanOddsControl key={`${target.id}-${target.outcome_id}`} field={field} value={value} current={outcomeReference(catalog, target)?.outcome.odds} disabled={disabled} onChange={onChange} />
    case 'interval_seconds':
      return <IntervalSecondsControl key={target.id} field={field} value={value} disabled={disabled} onChange={onChange} />
    case 'parlay_legs':
      return <ParlayLegsControl field={field} value={value} disabled={disabled} catalog={catalog} slug={slug} onChange={onChange} />
    default:
      return <Field {...fieldLabel(field)}><Input size="compact" value={value || ''} maxLength={field.max ?? 80} readOnly={isReadOnly} disabled={disabled} onChange={(event) => onChange(field.path, event.target.value)} /></Field>
  }
}

function inspectorFieldGroup(field) {
  if (field.path.startsWith('schedule.') || field.path === 'unavailable_behavior' || field.path === 'empty_behavior') return 'States'
  if (['layout', 'accent', 'card_density', 'metadata_style'].includes(field.path)) return 'Appearance'
  if (field.path.startsWith('event_selector.') || ['event_id', 'sport_ids', 'surface_id', 'page_size', 'legs', 'boosted_odds', 'suggested_queries', 'market_priority', 'market_count', 'competition.sport', 'competition.name', 'limit', 'scope', 'columns', 'player_limit', 'leagues'].includes(field.path)) return 'Data'
  if (field.path.startsWith('action') || ['autoplay', 'autoplay_seconds'].includes(field.path)) return 'Behavior'
  return 'Content'
}

function inspectorFieldVisible(target, field) {
  if (field.path === 'sport_ids' && target.collection) return false
  if (field.path === 'event_selector.event_id') return target.event_selector?.mode === 'manual'
  // Stacked showcases never auto-advance, so omit the control and any empty group.
  return field.path !== 'autoplay_seconds' || target.layout !== 'stack'
}

function InspectorFields({ screen, fields, selectedField, fieldSelectionId, collectionEditor, ...control }) {
  const fieldNodes = useRef(new Map())
  useEffect(() => {
    if (!selectedField || !fieldSelectionId) return
    const node = fieldNodes.current.get(selectedField)
    if (!node) return
    node.scrollIntoView({ block: 'center', behavior: 'instant' })
    const input = node.querySelector('input:not(:disabled), textarea:not(:disabled), select:not(:disabled)')
    const focusTarget = input || node.querySelector('button:not(:disabled)') || node
    focusTarget.focus({ preventScroll: true })
  }, [selectedField, fieldSelectionId])
  return ['Content', 'Appearance', 'Data', 'Behavior', 'States'].map((group) => {
    const members = fields.filter((field) => inspectorFieldVisible(control.target, field) && inspectorFieldGroup(field) === group)
    return members.length || group === 'Data' && collectionEditor ? <fieldset key={group} className={styles.inspectorGroup}>
      <legend>{group}</legend>
      <div className={styles.inspectorGroupFields}>{group === 'Data' ? collectionEditor : null}{members.map((field) => <div key={field.path} ref={(node) => { if (node) fieldNodes.current.set(field.path, node); else fieldNodes.current.delete(field.path) }} tabIndex={-1} data-inspection-field={field.path}>
        <EditorControl {...control} screen={screen} field={field} />
      </div>)}</div>
    </fieldset> : null
  })
}

function CardInspector({ selectedField, fieldSelectionId, sectionControls, component, card, cardIndex, cardCount, disabled, catalog, slug, onFieldChange, onMove, onRemove, onSelectComponent }) {
  const kind = cardKindDefinition(card.kind)
  const name = cardName(card)
  const schedule = cardScheduleState(card)
  return (
    <section className={`${styles.panel} ${styles.inspectorArea}`} aria-label="Inspector">
      <header className={styles.inspectorHeader}>
        <div className={styles.inspectorTitle}>
          <button type="button" className={styles.inspectorBack} onClick={() => onSelectComponent(component.id)}><ChevronLeft size={12} aria-hidden="true" />{componentInstanceName(component)}</button>
          <span className={styles.eyebrow}>{kind?.display_name || card.kind}</span>
          <h2>{name}</h2>
        </div>
        <div className={styles.inspectorActions}>
          <IconButton className={styles.rowIcon} label={`Move card ${name} up`} disabled={disabled || cardIndex === 0} onClick={() => onMove(cardIndex, -1)}><ArrowUp size={14} /></IconButton>
          <IconButton className={styles.rowIcon} label={`Move card ${name} down`} disabled={disabled || cardIndex >= cardCount - 1} onClick={() => onMove(cardIndex, 1)}><ArrowDown size={14} /></IconButton>
          <IconButton variant="danger" className={styles.rowIcon} label={`Remove card ${name}`} disabled={disabled || cardCount <= 1} onClick={() => onRemove(cardIndex)}><Trash2 size={14} /></IconButton>
        </div>
      </header>
      {schedule !== 'active' ? <p className={styles.compatNote} role="note">{schedule === 'scheduled' ? 'Scheduled: customers do not see this card yet.' : 'Expired: customers no longer see this card.'}</p> : null}
      <p className={styles.inspectorHint}>{kind?.description}</p>
      {sectionControls}
      <div className={styles.inspectorFields} key={card.id}>
        <InspectorFields selectedField={selectedField} fieldSelectionId={fieldSelectionId} fields={kind?.fields || []} slug={slug} target={card} disabled={disabled} catalog={catalog} onChange={(path, value) => onFieldChange(cardIndex, path, value)} />
      </div>
    </section>
  )
}

export function InspectorPanel({ screen = null, selectedField, fieldSelectionId, sectionControls, slug, component, index, count, card = null, cardIndex = -1, disabled = false, catalog = PREVIEW_CATALOG, onFieldChange, onChange, onMove, onRemove, onCardFieldChange, onMoveCard, onRemoveCard, onSelectComponent }) {
  if (!component) {
    return (
      <section className={`${styles.panel} ${styles.inspectorArea}`} aria-label="Inspector">
        <header className={styles.panelHeader}><h2>Inspector</h2></header>
        <p className={styles.inspectorEmpty}>Select a section in Layers or on the phone to edit it.</p>
      </section>
    )
  }
  if (card) {
    return <CardInspector selectedField={selectedField} fieldSelectionId={fieldSelectionId} sectionControls={sectionControls} component={component} card={card} cardIndex={cardIndex} cardCount={component.cards?.length || 0} disabled={disabled} catalog={catalog} slug={slug} onFieldChange={(position, path, value) => onCardFieldChange(index, position, path, value)} onMove={(position, direction) => onMoveCard(index, position, position + direction)} onRemove={(position) => onRemoveCard(index, position)} onSelectComponent={onSelectComponent} />
  }
  const definition = componentDefinition(component.type)
  const locked = !definition?.policy?.movable
  const name = componentInstanceName(component)
  const fields = definition?.editor?.fields || []
  return (
    <section className={`${styles.panel} ${styles.inspectorArea}`} aria-label="Inspector">
      <header className={styles.inspectorHeader}>
        <div className={styles.inspectorTitle}>
          <span className={styles.eyebrow}>{componentName(component)}</span>
          <h2>{name}</h2>
        </div>
        <div className={styles.inspectorActions}>
          {!locked ? <IconButton className={styles.rowIcon} label={`Move ${name} up`} disabled={disabled || index === 0} onClick={() => onMove(index, -1)}><ArrowUp size={14} /></IconButton> : null}
          {!locked ? <IconButton className={styles.rowIcon} label={`Move ${name} down`} disabled={disabled || index >= count - 2} onClick={() => onMove(index, 1)}><ArrowDown size={14} /></IconButton> : null}
          {definition?.policy?.removable ? <IconButton variant="danger" className={styles.rowIcon} label={`Remove ${name}`} disabled={disabled} onClick={() => onRemove(index)}><Trash2 size={14} /></IconButton> : null}
        </div>
      </header>
      {sectionControls}
      {component.type === 'hero' && catalog.events.some((event) => event.live) ? <p className={styles.compatNote} role="note">A live game is showing in this hero, so customers see the headline and button label over the live matchup. Body and destination appear only when no game is live.</p> : null}
      <div className={styles.inspectorFields} key={component.id}>
        <InspectorFields screen={screen} collectionEditor={component.type === 'event_list' ? <CollectionEditor component={component} catalog={catalog} slug={slug} disabled={disabled} onChange={(patch) => onChange(index, patch)} /> : null} fields={fields} slug={slug} target={component} disabled={disabled} catalog={catalog} onChange={(path, value) => onFieldChange(index, path, value)} />
        {fields.length === 0 ? <p className={styles.inspectorHint}>{definition?.description}</p> : null}
        {Array.isArray(component.cards) ? <p className={styles.inspectorHint}>{component.cards.length} card{component.cards.length === 1 ? '' : 's'}. Select a card in Layers or on the phone to edit it.</p> : null}
      </div>
      <div className={styles.panelFooter}>
        {definition?.policy?.required
          ? <span className={styles.lockNote}><Lock size={12} aria-hidden="true" /> Required. Stays at the end of Home.</span>
          : <Switch label="Visible on Home" checked={component.enabled !== false} disabled={disabled} onChange={(checked) => onChange(index, { enabled: checked })} />}
      </div>
    </section>
  )
}
